import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogTitle,
  DialogActions,
  useTheme,
  alpha,
  IconButton,
  Typography,
  Box,
  Paper
} from "@mui/material";
import remarkGfm from "remark-gfm";
import CodeBlock from "./chat/CodeBlock";
import ChatImage from "./chat/ChatImage";
import ReactMarkdown from "react-markdown";
import { useChatOptions } from "../hooks/useChatOptions";
import { FormFactor } from "../types/enums/FormFactor";
import { useFormFactor } from "../hooks/useFormFactor";
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';

export type HelpDialogProps = {
  open: boolean;
  onClose: () => void;
};

export default function HelpDialog({ open, onClose }: HelpDialogProps) {
  const { chatOptions } = useChatOptions();
  const formFactor = useFormFactor();
  const theme = useTheme();
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth={true}
      fullScreen={formFactor < FormFactor.Tablet}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: theme.palette.primary.main, 
          color: theme.palette.primary.contrastText,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HelpIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div">
            Help
          </Typography>
        </Box>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2
          }}
        >
          <ReactMarkdown
            className="markdown"
            remarkPlugins={[remarkGfm]}
            components={{
              // @ts-ignore
              pre: CodeBlock,
              img: ChatImage,
              a: ({ ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              ),
            }}
          >
            {chatOptions.help_text}
          </ReactMarkdown>
        </Paper>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Button 
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
